import { Link } from '@inertiajs/react'
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { ChevronRight } from 'lucide-react'
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion'

interface AnimatedLandingHeroProps {
  user?: {
    id: number
    name: string
    email: string
    username?: string
    role?: 'user' | 'admin'
  }
}

// Letter-by-letter animation component
function AnimatedWord({ word, className }: { word: string; className?: string }) {
  const letters = word.split('')
  
  return (
    <span className={className}>
      {letters.map((letter, index) => (
        <motion.span
          key={index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            delay: index * 0.08,
            ease: [0.25, 0.1, 0.25, 1.0]
          }}
          style={{ display: 'inline-block' }}
        >
          {letter}
        </motion.span>
      ))}
    </span>
  )
}

// Random letter reveal with Framer Motion
function RandomLetterReveal({ word }: { word: string }) {
  const [revealedLetters, setRevealedLetters] = useState<Set<number>>(new Set())
  const [isMounted, setIsMounted] = useState(false)
  const letters = word.split('')
  
  // Track client-side hydration
  useEffect(() => {
    setIsMounted(true)
  }, [])
  
  useEffect(() => {
    if (!isMounted) return
    
    const remaining = letters.map((_, i) => i)
    
    const revealNext = () => {
      if (remaining.length === 0) return
      
      const randomIndex = Math.floor(Math.random() * remaining.length)
      const letterIndex = remaining[randomIndex]
      remaining.splice(randomIndex, 1)
      
      setRevealedLetters(prev => new Set([...prev, letterIndex]))
      
      if (remaining.length > 0) {
        setTimeout(revealNext, 75)
      }
    }
    
    const timer = setTimeout(revealNext, 100)
    return () => clearTimeout(timer)
  }, [word, isMounted])
  
  return (
    <span className="inline text-orange-400">
      {letters.map((letter, index) => (
        <motion.span
          key={index}
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: isMounted && revealedLetters.has(index) ? 1 : 0 
          }}
          transition={{
            duration: 1,
            ease: 'easeOut'
          }}
          style={{ display: 'inline-block' }}
        >
          {letter}
        </motion.span>
      ))}
    </span>
  )
}

export function AnimatedLandingHero({ user }: AnimatedLandingHeroProps) {
  const { scrollY } = useScroll()
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0])
  const heroScale = useTransform(scrollY, [0, 400], [1, 0.95])
  
  const [showDeepThought, setShowDeepThought] = useState(false)
  const [deepThoughtComplete, setDeepThoughtComplete] = useState(false)
  const [typedText, setTypedText] = useState('')
  const [isMounted, setIsMounted] = useState(false)
  const fullText = 'Deep Tech Meets '
  const typingComplete = typedText.length === fullText.length
  
  // Track client-side hydration
  useEffect(() => {
    setIsMounted(true)
  }, [])
  
  // Typewriter effect - only runs on client after mounting
  useEffect(() => {
    if (!isMounted) return
    
    if (typingComplete) {
      const timer = setTimeout(() => setShowDeepThought(true), 300)
      return () => clearTimeout(timer)
    }
    
    const timer = setTimeout(() => {
      setTypedText(fullText.slice(0, typedText.length + 1))
    }, 80)
    
    return () => clearTimeout(timer)
  }, [typedText, typingComplete, isMounted])
  
  // Track completion
  useEffect(() => {
    if (showDeepThought && !deepThoughtComplete) {
      const timer = setTimeout(() => setDeepThoughtComplete(true), 1500)
      return () => clearTimeout(timer)
    }
  }, [showDeepThought, deepThoughtComplete])

  return (
    <motion.section 
      className="relative min-h-screen flex items-center -mt-16 overflow-hidden"
      style={{ opacity: heroOpacity }}
    >
      {/* Background image with overlay and subtle breathing */}
      <motion.div 
        className="absolute inset-0 z-0"
        animate={{
          opacity: [0.88, 1.0, 0.88],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
        style={{
          backgroundImage: 'url(/images/bg-good.webp)',
          backgroundSize: 'cover',
          backgroundPosition: '70% top',
          backgroundRepeat: 'no-repeat',
        }}
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 z-0 bg-linear-to-b from-background/50 via-background/40 to-muted/10" />

      <div className="container relative mx-auto px-4 text-center">
        {/* Hide entire content until client-side hydration to prevent any FOUC */}
        <div className={isMounted ? '' : 'invisible'}>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground mb-6 max-w-4xl mx-auto min-h-[120px]">
            {/* Typewriter effect */}
            <span className="inline">
              {typedText.split('').map((char, index) => (
                <motion.span
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.1 }}
                >
                  {char}
                </motion.span>
              ))}
              {/* Only show cursor after client-side hydration to prevent FOUC */}
              {!typingComplete && (
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                >|</motion.span>
              )}
            </span>
            <br></br>
            
            {/* "Deep Thought" with Framer Motion */}
            {showDeepThought && (
              <span className="inline">
                <RandomLetterReveal word="Deep Thought" />
              </span>
            )}
          </h1>
        
        {/* Description with motion */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ 
            opacity: deepThoughtComplete ? 1 : 0, 
            y: deepThoughtComplete ? 0 : 30 
          }}
          transition={{ 
            duration: 0.8, 
            ease: 'easeOut',
            delay: 0.3
          }}
        >
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-4 leading-relaxed">
            AI is our mirror of consciousness. Mastery of it can feel like magic. 
            By weaving mindful awareness with deep technical skill, we create intelligent systems 
            that channel both wisdom and wonder, like spells written in code.
          </p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center mt-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            {user ? (
              <>
                <Button size="lg" asChild className="bg-primary hover:bg-primary/90">
                  <Link href="/dashboard">
                    Go to Dashboard
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link href="/blog">Browse Posts</Link>
                </Button>
              </>
            ) : (
              <>
                <Button size="lg" asChild className="bg-primary hover:bg-primary/90">
                  <Link href="/blog">
                    Explore the Blog
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link href="/sign_up">
                    Join for Free
                  </Link>
                </Button>
              </>
            )}
          </motion.div>
          
          <motion.p 
            className="text-sm text-muted-foreground/60 mt-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: deepThoughtComplete ? 0.6 : 0 }}
            transition={{ delay: 0.8 }}
          >
            Members get access to exclusive posts and early insights
          </motion.p>
        </motion.div>
        </div>
      </div>
    </motion.section>
  )
}
